import { Card } from './Card';
import { Asset, PriceStatus } from '@/stores/assetStore';
import { useTranslation } from 'react-i18next';
import { LoadingIndicator } from './LoadingIndicator';
import { memo, useMemo } from 'react';
import { getAssetTypeConfig } from '@/utils/assetTypeConfig';
import { formatPrice } from '@/utils/formatPrice';
import { aktoolsService } from '@/services/aktools';

interface AssetCardProps {
  asset: Asset;
  onClick?: () => void;
}

// 从 URL 解析数据来源文本
const getDataSource = (sourceUrl?: string): string => {
  if (!sourceUrl) {
    return 'Unknown';
  }
  
  // 特殊处理：识别 Aktools 的 URL
  // 从 aktools service 获取 base URL 进行比较
  try {
    const aktoolsBaseUrl = aktoolsService.getBaseUrl();
    // 只有当 baseUrl 不为空时才进行检查
    if (aktoolsBaseUrl && aktoolsBaseUrl.trim() !== '') {
      // 处理相对路径（开发环境：/aktools）和绝对路径（生产环境：http://...）
      if (aktoolsBaseUrl.startsWith('/')) {
        // 开发环境：检查是否以 base URL 开头
        if (sourceUrl.startsWith(aktoolsBaseUrl)) {
          return 'Aktools';
        }
      } else {
        // 生产环境：检查是否以 base URL 开头
        if (sourceUrl.startsWith(aktoolsBaseUrl)) {
          return 'Aktools';
        }
        // 也检查是否包含 base URL 的 hostname 和 port（处理带路径的情况）
        try {
          const baseUrlObj = new URL(aktoolsBaseUrl);
          const sourceUrlObj = new URL(sourceUrl);
          if (baseUrlObj.hostname === sourceUrlObj.hostname && 
              baseUrlObj.port === sourceUrlObj.port) {
            return 'Aktools';
          }
        } catch {
          // 如果 URL 解析失败，继续使用其他逻辑
        }
      }
    }
  } catch {
    // 如果获取 base URL 失败，继续使用其他逻辑
  }
  
  try {
    const url = new URL(sourceUrl);
    let hostname = url.hostname;
    
    // 移除 www. 前缀（如果有）
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }
    
    // 移除 api. 前缀（如果有）
    if (hostname.startsWith('api.')) {
      hostname = hostname.substring(4);
    }
    
    // 获取主域名（处理子域名情况）
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      // 取最后两部分（例如：twelvedata.com）
      const domain = parts.slice(-2).join('.');
      // 首字母大写
      return domain.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join('.');
    }
    
    // 如果无法解析，返回原始 hostname（首字母大写）
    return hostname.charAt(0).toUpperCase() + hostname.slice(1);
  } catch (error) {
    // 如果 URL 解析失败，尝试简单处理
    let source = sourceUrl;
    if (source.startsWith('https://')) {
      source = source.substring(8);
    } else if (source.startsWith('http://')) {
      source = source.substring(7);
    }
    
    // 移除 api. 前缀
    if (source.startsWith('api.')) {
      source = source.substring(4);
    }
    
    // 获取域名部分
    const domain = source.split('/')[0];
    if (!domain) {
      return 'Unknown';
    }
    const parts = domain.split('.');
    if (parts.length >= 2) {
      const mainDomain = parts.slice(-2).join('.');
      return mainDomain.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join('.');
    }
    
    return domain.charAt(0).toUpperCase() + domain.slice(1);
  }
};

const AssetCardComponent = ({ asset, onClick }: AssetCardProps) => {
  const { t } = useTranslation();
  
  // Memoize expensive calculations
  const isPositive = useMemo(() => (asset.changePercent24h ?? 0) >= 0, [asset.changePercent24h]);
  const changeColor = useMemo(() => isPositive ? 'text-green-500' : 'text-red-500', [isPositive]);
  const typeInfo = useMemo(() => getAssetTypeConfig(asset.type), [asset.type]);
  // Always use English label for asset type tags
  const typeLabel = useMemo(() => typeInfo.label, [typeInfo]);

  // Memoize price validation
  const isValidPrice = useMemo(() => 
    asset.price !== undefined && 
    asset.price !== null && 
    !isNaN(asset.price) && 
    isFinite(asset.price),
    [asset.price]
  );

  // Memoize price status determination
  const priceStatus: PriceStatus = useMemo(() => 
    asset.priceStatus ?? (isValidPrice ? 'success' : 'loading'),
    [asset.priceStatus, isValidPrice]
  );

  // Format last updated time: M/D H:MM
  const formatLastUpdated = (date: Date | string | undefined): string | null => {
    if (!date) return null;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;
    const month = dateObj.getMonth() + 1; // No leading zero
    const day = dateObj.getDate(); // No leading zero
    const hours = dateObj.getHours(); // No leading zero
    const minutes = dateObj.getMinutes().toString().padStart(2, '0'); // Leading zero for minutes
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const formattedUpdateTime = formatLastUpdated(asset.lastUpdated);

  // For forex assets, extract the target currency from symbol (e.g., "USD/CNY" -> "CNY")
  const getForexTargetCurrency = (symbol: string): string => {
    if (asset.type === 'forex' && symbol.includes('/')) {
      const parts = symbol.split('/');
      return parts.length === 2 && parts[1] ? parts[1].trim() : '';
    }
    return '';
  };

  const forexTargetCurrency = getForexTargetCurrency(asset.symbol || '');

  return (
    <Card {...(onClick && { onClick })} className="mb-3 relative">
      {/* 右上角更新时间 */}
      {formattedUpdateTime && (
        <div className="absolute top-2 right-2 z-10">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('portfolio.updateAt')} {formattedUpdateTime}
          </span>
        </div>
      )}

      {/* 右下角数据来源 */}
      <div className="absolute bottom-2 right-2 z-10">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {getDataSource(asset.sourceUrl)}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
              {asset.name}
            </h3>
            <div className="flex items-center gap-1 relative">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {asset.symbol}
              </span>
              {/* 资产类型标签 - 使用投资组合页面样式 */}
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeInfo.color}`}>
                {typeLabel}
              </span>
            </div>
          </div>
          {priceStatus === 'success' && isValidPrice && asset.price !== undefined ? (
            <div className="mt-2">
              <div className="text-2xl font-bold text-gray-900 dark:text-white font-sans">
                {(() => {
                  // Don't show $ for index, forex, or CN/HK stocks (they use local currency)
                  const isIndexOrForex = asset.type === 'index' || asset.type === 'forex';
                  const isCnOrHkStock = asset.type === 'stock' && (
                    asset.exchange === 'HK' || 
                    asset.exchange === 'SH' || 
                    asset.exchange === 'SZ' || 
                    asset.exchange === 'BJ' ||
                    asset.symbol.toUpperCase().endsWith('.HK') ||
                    asset.symbol.toUpperCase().endsWith('.SH') ||
                    asset.symbol.toUpperCase().endsWith('.SZ') ||
                    asset.symbol.toUpperCase().endsWith('.BJ')
                  );
                  if (isIndexOrForex || isCnOrHkStock) {
                    return <>{formatPrice(asset.price)}</>;
                  }
                  return <>${formatPrice(asset.price)}</>;
                })()}
              </div>
              <div className={`text-sm font-medium mt-1 ${changeColor}`}>
                {isPositive ? '+' : ''}
                {asset.changePercent24h?.toFixed(2)}% ({isPositive ? '+' : ''}
                {(() => {
                  // Don't show $ for index, forex, or CN/HK stocks (they use local currency)
                  const isIndexOrForex = asset.type === 'index' || asset.type === 'forex';
                  const isCnOrHkStock = asset.type === 'stock' && (
                    asset.exchange === 'HK' || 
                    asset.exchange === 'SH' || 
                    asset.exchange === 'SZ' || 
                    asset.exchange === 'BJ' ||
                    asset.symbol.toUpperCase().endsWith('.HK') ||
                    asset.symbol.toUpperCase().endsWith('.SH') ||
                    asset.symbol.toUpperCase().endsWith('.SZ') ||
                    asset.symbol.toUpperCase().endsWith('.BJ')
                  );
                  if (isIndexOrForex || isCnOrHkStock) {
                    return <>{formatPrice(Math.abs(asset.change24h || 0))}</>;
                  }
                  return <>${formatPrice(Math.abs(asset.change24h || 0))}</>;
                })()})
              </div>
            </div>
          ) : priceStatus === 'error' ? (
            <div className="mt-2 mb-[15px] pr-[28px]">
              <div className="text-sm text-red-500 dark:text-red-400">
                加载失败
              </div>
            </div>
          ) : (
            <div className="text-gray-400 dark:text-gray-500 text-sm mt-2 flex items-center gap-2">
              <LoadingIndicator 
                type="spinner" 
                size="sm" 
                aria-label="Loading price data"
              />
              <span>{t('app.loading')}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const AssetCard = memo(AssetCardComponent, (prevProps, nextProps) => {
  // Custom comparison function for optimal re-rendering
  return (
    prevProps.asset.id === nextProps.asset.id &&
    prevProps.asset.price === nextProps.asset.price &&
    prevProps.asset.changePercent24h === nextProps.asset.changePercent24h &&
    prevProps.asset.change24h === nextProps.asset.change24h &&
    prevProps.asset.priceStatus === nextProps.asset.priceStatus &&
    prevProps.onClick === nextProps.onClick
  );
});
